import { getLocation, getPlaceMetaDescription } from "@Pages/PropertyPage/data/helper";
import { getLink } from "@Pages/Search/components/SearchListItem/helper";
import getImagePath from "@Utils/getImagePath";
import urljoin from "helpers/urljoin";
import { getPricingCurrencyStandardName, getFeaturedImage } from "../RoomTypeSection/utils";
import Config from "@Config/index";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import { getPricingCurrency } from "../PriceTrends/helpers";
import wrapperHOC from "@Utils/wrapperHOC";
import getReviewAuthorName from "./getReviewAuthorName";

const SectionSchema = () => {
  const { data, reviews, nonce } = useSelector(
    ({ propertyPageData, appData }: AppState) => ({
      data: propertyPageData.data,
      reviews: propertyPageData.reviewData,
      nonce: appData.nonce,
    }),
    shallowEqual,
  );

  const getApartmentComplexSchema = () => {
    const schemaData: any = {
      "@context": "https://schema.org",
      "@type": "ApartmentComplex",
    };

    // geo
    if (data.location_coordinates?.lat && data.location_coordinates?.lng) {
      schemaData.geo = {
        "@type": "GeoCoordinates",
        latitude: data.location_coordinates.lat,
        longitude: data.location_coordinates.lng,
      };
    }

    // address
    schemaData.address = {
      "@type": "PostalAddress",
    };
    const locationStreetAddress = getLocation(data.location, ["street_number", "route_long_name"]);
    const locationLocality = getLocation(data.location, ["locality_long_name"]);
    const locationState = getLocation(data.location, ["state_long_name"]);
    const locationCountry = getLocation(data.location, ["country_short_name"]);
    const locationPostalCode = getLocation(data.location, ["postal_code"]);
    if (locationStreetAddress) schemaData.address.streetAddress = locationStreetAddress;
    if (locationLocality) schemaData.address.addressLocality = locationLocality;
    if (locationState) schemaData.address.addressRegion = locationState;
    if (locationCountry) schemaData.address.addressCountry = locationCountry;
    if (locationPostalCode) schemaData.address.postalCode = locationPostalCode;

    return JSON.stringify(schemaData);
  };

  // https://developers.google.com/search/docs/advanced/structured-data/product
  const getSchema = () => {
    const schemaData: any = {
      "@context": "http://schema.org",
      "@type": "Product",
      name: data.name,
      description: getPlaceMetaDescription(data),
      url: urljoin(Config.WEB_SERVICE_URL, getLink(data)),
      sku: data.canonical_name,
      mpn: data.inventory_no,
      brand: {
        "@type": "Brand",
        name: "amberstudent",
      },
    };

    // offers
    let pricingCurrencyStandardName: any = "";
    if (data?.pricing?.currency)
      pricingCurrencyStandardName = getPricingCurrencyStandardName(data.pricing.currency);

    // Fixed pricing
    if (
      data.pricing?.price &&
      !data.pricing?.min_price &&
      !data.pricing?.max_price &&
      data.pricing?.currency &&
      pricingCurrencyStandardName
    ) {
      schemaData.offers = {
        "@type": "Offer",
        url: getLink(data),
        availability: "https://schema.org/LimitedAvailability",
        price: data.pricing.price,
        priceCurrency: pricingCurrencyStandardName,
        priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
      };
    }

    // Range pricing
    if (
      data.pricing?.min_price &&
      data.pricing?.max_price &&
      data.pricing?.currency &&
      pricingCurrencyStandardName
    ) {
      schemaData.offers = {
        "@type": "AggregateOffer",
        lowPrice: data.pricing.min_price,
        highPrice: data.pricing.max_price,
        offerCount: data?.children_short?.length || 1,
        priceCurrency: pricingCurrencyStandardName,
      };
    }

    // image
    if (data?.images?.length > 0) {
      const featuredImage = getFeaturedImage(data?.images);
      const featuredImagePath = getImagePath(featuredImage.path, "hero");
      if (featuredImagePath) schemaData.image = featuredImagePath;
    }

    // aggregateRating
    if (typeof data.reviews_count === "number" && data.reviews_count > 0 && data.reviews_rating) {
      schemaData.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: data.reviews_rating,
        reviewCount: data.reviews_count ?? 0,
        worstRating: 1,
        bestRating: 5,
      };
    }

    if (reviews?.length > 0) {
      schemaData.review = [];
      reviews.forEach((item: any) => {
        schemaData.review.push({
          "@type": "Review",
          reviewBody: item.content,
          author: {
            "@type": "Person",
            name: getReviewAuthorName(item),
          },
          reviewRating: {
            "@type": "Rating",
            ratingValue: item.rating,
            worstRating: 1,
            bestRating: 5,
          },
        });
      });
    }

    return JSON.stringify(schemaData);
  };

  const getLocalBusinessSchema = () => {
    const schemaData: any = {
      "@context": "http://schema.org",
      "@type": "LocalBusiness",
      name: data.name,
      url: urljoin(Config.WEB_SERVICE_URL, getLink(data)),
      "@id": urljoin(Config.WEB_SERVICE_URL, getLink(data)),
      telephone: "+918035735724 ",
    };

    // image
    if (data?.images?.length > 0) {
      const featuredImage = getFeaturedImage(data?.images);
      const featuredImagePath = getImagePath(featuredImage.path, "hero");
      if (featuredImagePath) schemaData.image = featuredImagePath;
    }

    // geo
    if (data.location_coordinates?.lat && data.location_coordinates?.lng) {
      schemaData.geo = {
        "@type": "GeoCoordinates",
        latitude: data.location_coordinates.lat,
        longitude: data.location_coordinates.lng,
      };
    }

    // address
    schemaData.address = {
      "@type": "PostalAddress",
    };
    const locationStreetAddress = getLocation(data.location, ["street_number", "route_long_name"]);
    const locationLocality = getLocation(data.location, ["locality_long_name"]);
    const locationState = getLocation(data.location, ["state_long_name"]);
    const locationCountry = getLocation(data.location, ["country_short_name"]);
    const locationPostalCode = getLocation(data.location, ["postal_code"]);
    if (locationStreetAddress) schemaData.address.streetAddress = locationStreetAddress;
    if (locationLocality) schemaData.address.addressLocality = locationLocality;
    if (locationState) schemaData.address.addressRegion = locationState;
    if (locationCountry) schemaData.address.addressCountry = locationCountry;
    if (locationPostalCode) schemaData.address.postalCode = locationPostalCode;

    // openingHoursSpecification
    schemaData.openingHoursSpecification = {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "18:00",
    };

    // offers
    let pricingCurrencySymbol: any = "";
    if (data?.pricing?.currency) pricingCurrencySymbol = getPricingCurrency(data.pricing.currency);

    // price range
    if (
      data.pricing?.price &&
      !data.pricing?.min_price &&
      !data.pricing?.max_price &&
      data.pricing?.currency &&
      pricingCurrencySymbol
    ) {
      const { price } = data.pricing;
      const priceCurrency = pricingCurrencySymbol;
      schemaData.priceRange = `${priceCurrency}${price}/week`;
    } else if (
      data.pricing?.min_price &&
      data.pricing?.max_price &&
      data.pricing?.currency &&
      pricingCurrencySymbol
    ) {
      const lowPrice = data.pricing.min_price;
      const highPrice = data.pricing.max_price;
      const priceCurrency = pricingCurrencySymbol;
      schemaData.priceRange = `${priceCurrency}${lowPrice}/week - ${priceCurrency}${highPrice}/week`;
    }

    return JSON.stringify(schemaData);
  };

  const getHotelSchema = () => {
    const schemaData: any = {
      "@context": "http://schema.org",
      "@type": "Hotel",
      name: data.name,
      url: urljoin(Config.WEB_SERVICE_URL, getLink(data)),
      telephone: "+44 7723497306",
      email: "contact@amberstudent.com",
      sameAs: [
        ...(Config.IS_CHINA_APP
          ? []
          : [
              "https://www.linkedin.com/company/amberstudent",
              "https://www.facebook.com/amberstudent",
              "https://www.instagram.com/amberstudent/",
            ]),
      ],
      checkinTime: "9:00",
      checkoutTime: "18:00",
    };

    // image
    if (data?.images?.length > 0) {
      const featuredImage = getFeaturedImage(data?.images);
      const featuredImagePath = getImagePath(featuredImage.path, "hero");
      if (featuredImagePath) schemaData.image = featuredImagePath;
    }

    // amenity features
    if (data?.features?.length > 0 && data?.features[0]?.values?.length > 0) {
      const amenityFeatures: any = [];

      data.features.forEach((feature: any) => {
        feature.values.forEach((value: any) => {
          amenityFeatures.push({
            "@type": "LocationFeatureSpecification",
            name: value?.name,
            value: true,
          });
        });
      });

      schemaData.amenityFeature = amenityFeatures;
    }

    // address
    schemaData.address = {
      "@type": "PostalAddress",
    };
    const locationStreetAddress = getLocation(data.location, ["street_number", "route_long_name"]);
    const locationLocality = getLocation(data.location, ["locality_long_name"]);
    const locationState = getLocation(data.location, ["state_long_name"]);
    const locationCountry = getLocation(data.location, ["country_short_name"]);
    const locationPostalCode = getLocation(data.location, ["postal_code"]);
    if (locationStreetAddress) schemaData.address.streetAddress = locationStreetAddress;
    if (locationLocality) schemaData.address.addressLocality = locationLocality;
    if (locationState) schemaData.address.addressRegion = locationState;
    if (locationCountry) schemaData.address.addressCountry = locationCountry;
    if (locationPostalCode) schemaData.address.postalCode = locationPostalCode;

    // offers
    let pricingCurrencySymbol: any = "";
    if (data?.pricing?.currency) pricingCurrencySymbol = getPricingCurrency(data.pricing.currency);

    // price range
    if (
      data.pricing?.price &&
      !data.pricing?.min_price &&
      !data.pricing?.max_price &&
      data.pricing?.currency &&
      pricingCurrencySymbol
    ) {
      const { price } = data.pricing;
      const priceCurrency = pricingCurrencySymbol;
      schemaData.priceRange = `${priceCurrency}${price}/week`;
    } else if (
      data.pricing?.min_price &&
      data.pricing?.max_price &&
      data.pricing?.currency &&
      pricingCurrencySymbol
    ) {
      const lowPrice = data.pricing.min_price;
      const highPrice = data.pricing.max_price;
      const priceCurrency = pricingCurrencySymbol;
      schemaData.priceRange = `${priceCurrency}${lowPrice}/week - ${priceCurrency}${highPrice}/week`;
    }

    return JSON.stringify(schemaData);
  };

  return (
    <div className="section-schema-container">
      {data.reviews_rating && (
        <script
          type="application/ld+json"
          nonce={nonce}
          dangerouslySetInnerHTML={{ __html: getSchema() }}
        />
      )}
      <script
        type="application/ld+json"
        nonce={nonce}
        dangerouslySetInnerHTML={{ __html: getApartmentComplexSchema() }}
      />
      <script
        type="application/ld+json"
        nonce={nonce}
        dangerouslySetInnerHTML={{ __html: getLocalBusinessSchema() }}
      />
      <script
        type="application/ld+json"
        nonce={nonce}
        dangerouslySetInnerHTML={{ __html: getHotelSchema() }}
      />
    </div>
  );
};

export default wrapperHOC(SectionSchema, {
  componentName: "SectionSchema-PropertyPage",
  showForChina: true,
});
